import { isAllowedProduct } from '@/data/catalogScope';
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";

export interface CartItem {
  id: number;
  name: string;
  price: number;
  image: string;
  quantity: number;
  variantId?: number;
  variantLabel?: string;
  maxQuantity?: number;
}

// Cada variação (cor, tamanho...) é uma linha própria da sacola: o mesmo produto
// em duas variações são dois itens, cada um com o seu variantId no pedido.
export function cartLineKey(item: Pick<CartItem, "id" | "variantId">): string {
  return `${item.id}:${item.variantId ?? 0}`;
}

interface CartContextValue {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
  addItem: (item: Omit<CartItem, "quantity">, quantity?: number) => void;
  removeItem: (key: string) => void;
  updateQuantity: (key: string, quantity: number) => void;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const addItem = useCallback((item: Omit<CartItem, "quantity">, quantity?: number) => {
    if (!isAllowedProduct(item.id)) return;
    setItems((prev) => {
      const key = cartLineKey(item);
      const existing = prev.find((entry) => cartLineKey(entry) === key);
      const clamp = (value: number, max?: number) => (max === undefined ? value : Math.min(value, max));
      if (existing) {
        return prev.map((entry) =>
          cartLineKey(entry) === key
            ? { ...entry, maxQuantity: item.maxQuantity ?? entry.maxQuantity, quantity: clamp(entry.quantity + (quantity ?? 1), item.maxQuantity ?? entry.maxQuantity) }
            : entry
        );
      }
      return [...prev, { ...item, quantity: Math.max(1, clamp(quantity ?? 1, item.maxQuantity)) }];
    });
  }, []);

  const removeItem = useCallback((key: string) => {
    setItems((prev) => prev.filter((entry) => cartLineKey(entry) !== key));
  }, []);

  const updateQuantity = useCallback((key: string, quantity: number) => {
    setItems((prev) =>
      quantity <= 0
        ? prev.filter((entry) => cartLineKey(entry) !== key)
        : prev.map((entry) =>
            cartLineKey(entry) === key
              ? { ...entry, quantity: entry.maxQuantity === undefined ? quantity : Math.min(quantity, entry.maxQuantity) }
              : entry
          )
    );
  }, []);

  const value = useMemo<CartContextValue>(() => {
    const totalItems = items.reduce((sum, entry) => sum + entry.quantity, 0);
    const totalPrice = items.reduce((sum, entry) => sum + entry.price * entry.quantity, 0);
    return {
      items,
      totalItems,
      totalPrice,
      addItem,
      removeItem,
      updateQuantity,
      isOpen,
      openCart: () => setIsOpen(true),
      closeCart: () => setIsOpen(false),
    };
  }, [items, isOpen, addItem, removeItem, updateQuantity]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within CartProvider");
  }
  return ctx;
}